/* import __COLOCATED_TEMPLATE__ from './verification-screen.hbs'; */
import Component from '@glimmer/component';
import { action, getProperties } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { restartableTask, timeout } from 'ember-concurrency';
import {
  PROJECTDIGEST_VERIFICATION_METHODS,
  PROJECTDIGEST_VERIFICATION_METHODS_REQUIRES_ADDITIONAL_INFO,
  VALID_ATTRIBUTION_SERVICE_IDS,
} from 'sh-www/models/projectdigest-verification';
import { escapeRegExp } from 'sh-www/utils/misc';
import { validateSchema } from 'sh-www/utils/validators';
import { number, object, string } from 'yup';
import clsx from 'clsx';
import Validator from 'validator';

const CLASS_NAME = 'GLM-c-modals-overlays-lightbox-verification-screen';
const EMAIL_MATCH_DEBOUNCE_MS = 300;
const FAILED_TO_LOAD_DOMAINS_ERROR = 'Failed to load potential company matches. Please refresh the page.';

const PROJECTDIGEST_VERIFICATION_METHOD_SCHEMAS = {
  [PROJECTDIGEST_VERIFICATION_METHODS.REFERRAL]: object({
    'referral_firstname': string()
      .min(2, 'First name must be at least 2 characters')
      .required('You must provide a first name.'),
    'referral_lastname': string()
      .min(2, 'Last name must be at least 2 characters')
      .required('You must provide a last name.'),
    'referral_email': string()
      .test(
        'email-requirement',
        'Please enter a valid email address.',
        function(value) {
          return Validator.isEmail(value);
        }
      )
      .required('You must provide an email address.'),
    'referral_company': string().required('A company name must be provided.'),
  }),
  [PROJECTDIGEST_VERIFICATION_METHODS.IMDB]: object({
    'link': string()
      .required('You must provide a link.')
      .test(
        'link-requirement',
        'Link must be a valid URL',
        function(value) {
          const protocols = ['http', 'https'];
          return value ? Validator.isURL(value, { protocols }) : true;
        }
      ),
  }),
  [PROJECTDIGEST_VERIFICATION_METHODS.TIMESTAMP]: object({
    'minutes': number()
      .required('You must provide the minute mark showing credit.')
      .typeError('Minutes must be a valid number.')
      .min(0, 'Minutes must be greater than or equal to zero.'),
    'seconds': number()
      .required('You must provide the second mark showing credit.')
      .typeError('Minutes must be a valid number.')
      .min(0, 'Seconds must be greater than or equal to zero.')
      .max(59, 'Seconds must be less than or equal to 59.'),
  }),
};

export const VERIFICATION_OVERLAY_FORM_INPUTS = [
  'generic-exception',
  'link',
  'minutes',
  'referral_brand_id',
  'referral_company',
  'referral_email',
  'referral_firstname',
  'referral_lastname',
  'seconds',
  'unhandled-exception',
];

const hasErrors = (errors) => {
  return Object.values(errors).some((error) => error !== undefined);
}

class GlmLightboxOverlayVerificationScreenComponent extends Component {
  @service('commands/domain-commands') domainCommandService;
  @service('error-tracker') errorTracker;
  @service media;

  @tracked referral_firstname = '';
  @tracked referral_lastname = '';
  @tracked referral_email = '';
  @tracked referral_company = '';
  @tracked link = '';
  @tracked minutes = 0;
  @tracked seconds = 0;
  @tracked verificationMethod = null;
  @tracked domainMatches = null;
  @tracked brandId = null;

  #matchingBrandOptions = createCache(() => {
    const { referral_company, domainMatchBrandOptions } = this;

    if (!referral_company) {
      return domainMatchBrandOptions;
    }

    const escapedInternalValue = escapeRegExp(referral_company);
    const pattern = new RegExp(`(\\b${escapedInternalValue})`, 'i');

    return domainMatchBrandOptions.filter(({ key }) => {
      return pattern.test(key);
    });
  });

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get domainMatchBrandOptions() {
    return (this.domainMatches?.brands ?? []).map((brand) => ({
        value: brand.id,
        key: brand.name,
      }));
  }

  get formDisabled() {
    return this.args.onSaveVerificationTask.isRunning;
  }

  get inferredBrandId() {
    const { brandId, referral_company } = this;
    const compareName = referral_company.trim().toLowerCase();

    return brandId
      ?? this.domainMatchBrandOptions.find(({ key }) => key.toLowerCase() === compareName)?.value
      ?? null;
  }

  get matchingBrandOptions() {
    return getValue(this.#matchingBrandOptions);
  }

  get showAttributionOption() {
    const { services } = this.args.selectedDigest;
    return services.toArray().some(({ id }) => VALID_ATTRIBUTION_SERVICE_IDS.includes(id));
  }

  get submitDisabled() {
    return !this.verificationMethod || this.formDisabled;
  }

  get verificationMethods() {
    return PROJECTDIGEST_VERIFICATION_METHODS;
  }

  get verificationMethodRequiresMoreInfo() {
    return PROJECTDIGEST_VERIFICATION_METHODS_REQUIRES_ADDITIONAL_INFO.has(this.verificationMethod);
  }

  @action
  handleCancel() {
    this.args.onCloseSecondaryScreen();
  }

  @action
  handleInput(name, value) {
    this.args.updateError(name, undefined);

    this[name] = value;

    if (name === 'referral_email') {
      this.findCompanyMatchesFromEmail.perform(value);
    } else if (name === 'referral_company') {
      this.brandId = null;
    }
  }

  @action
  handleBrandOptionClick({ key, value }) {
    this.args.updateError('referral_company', undefined);

    this.brandId = value;
    this.referral_company = key;
  }

  /**
   * @param {string} method one of PROJECTDIGEST_VERIFICATION_METHODS.*
   */
  @action
  onVerificationMethodClicked(method) {
    this.args.clearErrors();

    this.verificationMethod = method;
  }

  @action
  submitVerificationForm(e) {
    e.preventDefault();

    this.args.clearErrors();

    const { verificationMethod } = this;
    if (!verificationMethod) {
      return;
    }

    const schema = PROJECTDIGEST_VERIFICATION_METHOD_SCHEMAS[verificationMethod];
    if (schema) {
      const [_, validationErrors = {}] = validateSchema(getProperties(this, Object.keys(schema.fields)), schema);
      this.args.setErrors(validationErrors);

      // prevent save while errors exist
      if (hasErrors(validationErrors)) {
        return;
      }
    }

    const { selectedDigest } = this.args;
    const data = { method: verificationMethod };

    // perform request based on method
    switch (verificationMethod) {
      case PROJECTDIGEST_VERIFICATION_METHODS.REFERRAL:
        const {
          referral_firstname: referralFirstName,
          referral_lastname: referralLastName,
          referral_email: referralEmail,
          referral_company: referralCompanyName,
          inferredBrandId: referralBrandId,
        } = this;

        this.args.onSaveVerificationTask.perform(selectedDigest, {
          ...data,
          referralFirstName,
          referralLastName,
          referralEmail,
          referralCompanyName,
          referralBrandId,
        });
        break;
      case PROJECTDIGEST_VERIFICATION_METHODS.IMDB:
        this.args.onSaveVerificationTask.perform(selectedDigest, { ...data, link: this.link, });
        break;
      case PROJECTDIGEST_VERIFICATION_METHODS.TIMESTAMP:
        this.args.onSaveVerificationTask.perform(selectedDigest, { ...data, minutes: this.minutes, seconds: this.seconds });
        break;
      case PROJECTDIGEST_VERIFICATION_METHODS.BYLINE_CREDIT:
        this.args.onSaveVerificationTask.perform(selectedDigest, data);
        break;
      default:
        this.args.updateError('generic-exception', 'Unsupported verification method');
        break;
    }
  }

  @restartableTask
  *findCompanyMatchesFromEmail(email) {
    this.domainMatches = null;

    if (!Validator.isEmail(email)) {
      return;
    }

    yield timeout(EMAIL_MATCH_DEBOUNCE_MS);

    try {
      this.domainMatches = yield this.domainCommandService.loadDomainForEmail(email);
    } catch (error) {
      // company not on platform
      if (error.status === 404) {
        return;
      }

      this.args.updateError('generic-exception', error.errors?.['generic-exception']?.[0] ?? FAILED_TO_LOAD_DOMAINS_ERROR);
      this.errorTracker.error(FAILED_TO_LOAD_DOMAINS_ERROR, this, error);
    }
  }
}

export default GlmLightboxOverlayVerificationScreenComponent;
